import {
  Archive,
  Check,
  ClipboardCheck,
  Lock,
  MailOpen,
  Network,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UserX,
  Users,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  OrgInvitationSummary,
  OrgMemberSummary,
  OrgUnitSummary,
} from '@nadoutong/contracts';
import { useEffect, useMemo, useState } from 'react';
import { PlatformApiError } from '../api/client.ts';
import {
  useArchiveOrgUnit,
  useCreateOrgUnit,
  useOrgInvitations,
  useOrgMembers,
  useOrgUnits,
  useUpdateOrgUnit,
  useUpdateOrgMember,
} from '../api/useOrgs.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { formatTimestamp } from '../components/formatters.ts';
import { hasPermission, useSession } from '../hooks/useSession.tsx';

type TabId = 'members' | 'structure' | 'invitations' | 'governance';
type UnitFilter = 'all' | 'unassigned' | string;

const COPY = {
  title: 'Organization',
  eyebrow: 'Member directory',
  subtitle: 'Assign members to org units; usage views use this assignment first.',
  tabs: {
    members: 'Members',
    structure: 'Structure',
    invitations: 'Invitations',
    governance: 'Governance',
  },
  archive: 'Archive',
  cancel: 'Cancel',
  create: 'Create',
  edit: 'Edit',
  refresh: 'Refresh',
  save: 'Save',
  search: 'Search name, email, org unit',
  allUnits: 'All org units',
  unassigned: 'Unassigned',
  members: 'Members',
  units: 'Org units',
  pending: 'Pending invites',
  member: 'Member',
  unit: 'Org unit',
  role: 'Role',
  access: 'Access',
  joined: 'Joined',
  roleLabel: {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
  },
  limited: 'Limited access',
  unrestricted: 'Unrestricted',
  loading: 'Loading organization data…',
  membersError: 'Could not load members',
  unitsError: 'Could not load org units',
  invitationsError: 'Could not load invitations',
  emptyMembers: 'No members yet.',
  noInvitations: 'No pending invitations.',
  forbidden: 'Only owners and admins can view organization management.',
  detailTitle: 'Org unit detail',
  noUnit: 'Select an org unit to inspect assignments.',
  parent: 'Parent',
  directMembers: 'Direct members',
  unitStatus: 'Status',
  structureTitle: 'Structure',
  structureSubtitle: 'Review org units by path and direct member count.',
  structureEmpty: 'No org units yet.',
  structureCreateTitle: 'New org unit',
  structureName: 'Name',
  structureNamePlaceholder: 'Engineering',
  structureParent: 'Parent',
  structureRootParent: 'Top-level org unit',
  invitationsTitle: 'Pending invitations',
  invitationsSubtitle: 'Valid invites that have not been accepted or revoked.',
  governanceTitle: 'Governance overview',
  governanceSubtitle: 'Assignment coverage, limits, and manager scope.',
  assigned: 'Assigned members',
  limitedMembers: 'Limited access members',
  ownerAdmin: 'Owners / admins',
} as const;

export function Organization(): JSX.Element {
  const { user } = useSession();
  const orgId = user?.orgId ?? undefined;
  const canReadMembers = hasPermission(user, 'org:members:read');
  const canManageMembers = hasPermission(user, 'org:members:manage');
  const membersQ = useOrgMembers(canReadMembers ? orgId : undefined);
  const unitsQ = useOrgUnits(canReadMembers ? orgId : undefined);
  const invitationsQ = useOrgInvitations(canReadMembers ? orgId : undefined);
  const updateMember = useUpdateOrgMember();
  const createOrgUnit = useCreateOrgUnit();
  const updateOrgUnit = useUpdateOrgUnit();
  const archiveOrgUnit = useArchiveOrgUnit();
  const [activeTab, setActiveTab] = useState<TabId>('members');
  const [search, setSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState<UnitFilter>('all');
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [createUnitName, setCreateUnitName] = useState('');
  const [createParentId, setCreateParentId] = useState('');
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [editUnitName, setEditUnitName] = useState('');
  const [editParentId, setEditParentId] = useState('');

  const members = membersQ.data?.members ?? [];
  const units = unitsQ.data?.units ?? [];
  const invitations = invitationsQ.data?.invitations ?? [];
  const defaultUnitId = useMemo(() => pickDefaultUnitId(units), [units]);

  useEffect(() => {
    if (units.length === 0) {
      setSelectedUnitId(null);
      return;
    }
    if (selectedUnitId === null || !units.some((unit) => unit.id === selectedUnitId)) {
      setSelectedUnitId(defaultUnitId);
    }
  }, [defaultUnitId, selectedUnitId, units]);

  const selectedUnit = units.find((unit) => unit.id === selectedUnitId) ?? null;
  const filteredMembers = useMemo(
    () => filterMembers(members, search, unitFilter),
    [members, search, unitFilter],
  );
  const assignedMembers = members.filter((member) => member.orgUnitId !== null).length;
  const limitedMembers = members.filter((member) => member.monthlyLimitUsdCents !== null).length;
  const managerMembers = members.filter((member) => member.role === 'owner' || member.role === 'admin').length;
  const anyLoading = membersQ.isLoading || unitsQ.isLoading || invitationsQ.isLoading;

  function refreshAll(): void {
    void membersQ.refetch();
    void unitsQ.refetch();
    void invitationsQ.refetch();
  }

  function handleAssignment(member: OrgMemberSummary, nextOrgUnitId: string): void {
    if (!canManageMembers || orgId === undefined) {
      return;
    }
    updateMember.mutate({
      orgId,
      accountId: member.accountId,
      body: {
        primaryOrgUnitId: nextOrgUnitId.length === 0 ? null : nextOrgUnitId,
      },
    });
  }

  function handleCreateUnit(): void {
    if (!canManageMembers || orgId === undefined) {
      return;
    }
    const name = createUnitName.trim();
    if (name.length === 0) {
      return;
    }
    createOrgUnit.mutate(
      {
        orgId,
        body: {
          name,
          parentId: createParentId.length === 0 ? null : createParentId,
        },
      },
      {
        onSuccess: (data) => {
          setCreateUnitName('');
          setCreateParentId('');
          setSelectedUnitId(data.unit.id);
        },
      },
    );
  }

  function handleStartEditUnit(unit: OrgUnitSummary): void {
    setEditingUnitId(unit.id);
    setEditUnitName(unit.name);
    setEditParentId(unit.parentId ?? '');
  }

  function handleCancelEditUnit(): void {
    setEditingUnitId(null);
    setEditUnitName('');
    setEditParentId('');
  }

  function handleSaveUnit(unit: OrgUnitSummary): void {
    if (!canManageMembers || orgId === undefined) {
      return;
    }
    const name = editUnitName.trim();
    if (name.length === 0) {
      return;
    }
    updateOrgUnit.mutate(
      {
        orgId,
        orgUnitId: unit.id,
        body: {
          name,
          parentId: editParentId.length === 0 ? null : editParentId,
        },
      },
      {
        onSuccess: (data) => {
          handleCancelEditUnit();
          setSelectedUnitId(data.unit.id);
        },
      },
    );
  }

  function handleArchiveUnit(unit: OrgUnitSummary): void {
    if (!canManageMembers || orgId === undefined) {
      return;
    }
    archiveOrgUnit.mutate(
      { orgId, orgUnitId: unit.id },
      {
        onSuccess: () => {
          if (selectedUnitId === unit.id) {
            setSelectedUnitId(null);
          }
        },
      },
    );
  }

  if (!canReadMembers) {
    return (
      <section data-testid="organization-page" className="flex flex-col gap-4">
        <h2 className="font-display text-display text-ink">{COPY.title}</h2>
        <p className="rounded-lg border border-line bg-card p-4 text-body text-ink-low">
          {COPY.forbidden}
        </p>
      </section>
    );
  }

  return (
    <section data-testid="organization-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex max-w-3xl flex-col gap-2">
          <span className="inline-flex w-fit items-center gap-2 font-mono text-caption font-medium uppercase text-ink-low">
            <span aria-hidden="true" className="h-2 w-2 rounded-sm bg-rust" />
            {COPY.eyebrow}
          </span>
          <h2 className="font-display text-display text-ink">{COPY.title}</h2>
          <p className="text-body leading-6 text-ink-mid">{COPY.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={refreshAll}
          title={COPY.refresh}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-line bg-card px-4 py-2 text-body font-medium text-ink transition-colors hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        >
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          <span>{COPY.refresh}</span>
        </button>
      </header>

      <div
        data-testid="organization-tabs"
        className="flex flex-wrap gap-2 rounded-lg border border-line bg-raised p-1"
      >
        {(Object.keys(COPY.tabs) as TabId[]).map((tab) => (
          <button
            type="button"
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={[
              'inline-flex items-center gap-2 rounded-md px-3 py-2 text-body font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
              activeTab === tab
                ? 'bg-active text-forest'
                : 'text-ink-low hover:bg-card hover:text-ink',
            ].join(' ')}
          >
            <TabIconGlyph tab={tab} />
            <span>{COPY.tabs[tab]}</span>
          </button>
        ))}
      </div>

      <SummaryRail
        memberCount={members.length}
        unitCount={unitsQ.data?.summary.totalUnits ?? units.length}
        unassignedCount={unitsQ.data?.summary.unassignedMembers ?? 0}
        pendingCount={invitations.length}
      />

      {anyLoading ? (
        <LoadingState label={COPY.loading} />
      ) : (
        <>
          {membersQ.isError ? <QueryError error={membersQ.error} fallback={COPY.membersError} /> : null}
          {unitsQ.isError ? <QueryError error={unitsQ.error} fallback={COPY.unitsError} /> : null}
          {invitationsQ.isError ? <QueryError error={invitationsQ.error} fallback={COPY.invitationsError} /> : null}
          {activeTab === 'members' ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
              <div className="flex min-w-0 flex-col gap-4">
                <DirectoryFilters
                  units={units}
                  search={search}
                  unitFilter={unitFilter}
                  onSearch={setSearch}
                  onUnitFilter={setUnitFilter}
                />
                <MemberDirectory
                  members={filteredMembers}
                  units={units}
                  canManageMembers={canManageMembers}
                  assignmentPending={updateMember.isPending}
                  onAssignment={handleAssignment}
                  onSelectUnit={setSelectedUnitId}
                />
              </div>
              <OrgUnitDetail unit={selectedUnit} units={units} />
            </div>
          ) : null}
          {activeTab === 'structure' ? (
            <StructurePanel
              units={units}
              canManageUnits={canManageMembers}
              createName={createUnitName}
              createParentId={createParentId}
              editingUnitId={editingUnitId}
              editName={editUnitName}
              editParentId={editParentId}
              mutationPending={
                createOrgUnit.isPending || updateOrgUnit.isPending || archiveOrgUnit.isPending
              }
              onCreateName={setCreateUnitName}
              onCreateParent={setCreateParentId}
              onCreateUnit={handleCreateUnit}
              onEditName={setEditUnitName}
              onEditParent={setEditParentId}
              onStartEdit={handleStartEditUnit}
              onCancelEdit={handleCancelEditUnit}
              onSaveUnit={handleSaveUnit}
              onArchiveUnit={handleArchiveUnit}
              onSelectUnit={setSelectedUnitId}
            />
          ) : null}
          {activeTab === 'invitations' ? (
            <InvitationsPanel invitations={invitations} />
          ) : null}
          {activeTab === 'governance' ? (
            <GovernancePanel
              assignedMembers={assignedMembers}
              limitedMembers={limitedMembers}
              managerMembers={managerMembers}
            />
          ) : null}
          {activeTab === 'members' ? <InvitationsPanel invitations={invitations} compact /> : null}
        </>
      )}
    </section>
  );
}

function SummaryRail({
  memberCount,
  unitCount,
  unassignedCount,
  pendingCount,
}: {
  readonly memberCount: number;
  readonly unitCount: number;
  readonly unassignedCount: number;
  readonly pendingCount: number;
}): JSX.Element {
  const items = [
    { label: COPY.members, value: memberCount, icon: Users },
    { label: COPY.units, value: unitCount, icon: Network },
    { label: COPY.unassigned, value: unassignedCount, icon: UserX },
    { label: COPY.pending, value: pendingCount, icon: MailOpen },
  ];
  return (
    <div
      data-testid="organization-summary"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-3 rounded-lg border border-line bg-card px-4 py-3">
          <item.icon aria-hidden="true" className="h-5 w-5 shrink-0 text-forest" />
          <div className="flex min-w-0 flex-col">
            <span className="text-caption text-ink-low">{item.label}</span>
            <span className="font-mono text-xl font-medium tabular-nums text-ink">
              {item.value}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function DirectoryFilters({
  units,
  search,
  unitFilter,
  onSearch,
  onUnitFilter,
}: {
  readonly units: readonly OrgUnitSummary[];
  readonly search: string;
  readonly unitFilter: UnitFilter;
  readonly onSearch: (value: string) => void;
  readonly onUnitFilter: (value: UnitFilter) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 rounded-lg border border-line bg-raised p-3 lg:grid-cols-[minmax(0,1fr)_16rem]">
      <label className="flex min-w-0 items-center gap-2 rounded-md border border-line bg-card px-3 py-2 focus-within:ring-2 focus-within:ring-forest-bright/40">
        <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-ink-low" />
        <input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={COPY.search}
          className="min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-ink-faint"
        />
      </label>
      <select
        value={unitFilter}
        onChange={(event) => onUnitFilter(event.target.value)}
        className="rounded-md border border-line bg-card px-3 py-2 text-body font-medium text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40"
      >
        <option value="all">{COPY.allUnits}</option>
        <option value="unassigned">{COPY.unassigned}</option>
        {units.map((unit) => (
          <option key={unit.id} value={unit.id}>
            {unit.path}
          </option>
        ))}
      </select>
    </div>
  );
}

function MemberDirectory({
  members,
  units,
  canManageMembers,
  assignmentPending,
  onAssignment,
  onSelectUnit,
}: {
  readonly members: readonly OrgMemberSummary[];
  readonly units: readonly OrgUnitSummary[];
  readonly canManageMembers: boolean;
  readonly assignmentPending: boolean;
  readonly onAssignment: (member: OrgMemberSummary, nextOrgUnitId: string) => void;
  readonly onSelectUnit: (id: string | null) => void;
}): JSX.Element {
  if (members.length === 0) {
    return (
      <p className="rounded-lg border border-line bg-card p-4 text-body text-ink-low">
        {COPY.emptyMembers}
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-card">
      <div className="grid grid-cols-[minmax(15rem,1.5fr)_minmax(12rem,1fr)_7rem_9rem_8rem] gap-4 border-b border-line bg-active px-4 py-3 text-left text-caption font-medium uppercase text-ink-low">
        <span>{COPY.member}</span>
        <span>{COPY.unit}</span>
        <span>{COPY.role}</span>
        <span>{COPY.access}</span>
        <span>{COPY.joined}</span>
      </div>
      <div className="divide-y divide-line-soft overflow-x-auto">
        {members.map((member) => (
          <div
            key={member.accountId}
            data-testid={`organization-member-row-${member.accountId}`}
            className="grid min-w-[58rem] grid-cols-[minmax(15rem,1.5fr)_minmax(12rem,1fr)_7rem_9rem_8rem] items-center gap-4 px-4 py-3 text-body"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-forest font-medium text-on-forest">
                {avatarInitial(member)}
              </span>
              <div className="min-w-0">
                <p className="truncate font-medium text-ink">
                  {member.displayName ?? member.email}
                </p>
                <p className="truncate font-mono text-caption text-ink-low">{member.email}</p>
              </div>
            </div>
            <div className="flex min-w-0 items-center">
              {canManageMembers ? (
                <select
                  value={member.orgUnitId ?? ''}
                  disabled={assignmentPending}
                  onChange={(event) => onAssignment(member, event.target.value)}
                  title={COPY.unit}
                  className="min-w-0 w-full rounded-md border border-line bg-raised px-2 py-1 text-caption font-medium text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40 disabled:opacity-50"
                >
                  <option value="">{COPY.unassigned}</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.path}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  type="button"
                  onClick={() => onSelectUnit(member.orgUnitId)}
                  className="min-w-0 w-full truncate rounded-md bg-raised px-2 py-1 text-left font-mono text-caption text-ink-mid hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                >
                  {member.orgUnitPath ?? member.department ?? COPY.unassigned}
                </button>
              )}
            </div>
            <span className="text-ink-mid">{COPY.roleLabel[member.role]}</span>
            <span className="inline-flex w-fit rounded-md bg-raised px-2 py-1 text-caption font-medium text-ink">
              {member.monthlyLimitUsdCents === null ? COPY.unrestricted : COPY.limited}
            </span>
            <span className="font-mono text-caption text-ink-low">{formatDate(member.joinedAt)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrgUnitDetail({
  unit,
  units,
}: {
  readonly unit: OrgUnitSummary | null;
  readonly units: readonly OrgUnitSummary[];
}): JSX.Element {
  const parent = unit?.parentId === null ? null : units.find((candidate) => candidate.id === unit?.parentId) ?? null;
  return (
    <aside
      data-testid="organization-unit-detail"
      className="flex min-h-[18rem] flex-col gap-4 rounded-lg border border-line bg-card p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-body font-medium text-ink">{COPY.detailTitle}</h3>
        <Network aria-hidden="true" className="h-5 w-5 text-forest" />
      </div>
      {unit === null ? (
        <p className="text-body leading-6 text-ink-low">{COPY.noUnit}</p>
      ) : (
        <>
          <div>
            <p className="text-lg font-medium text-ink">{unit.name}</p>
            <p className="mt-1 font-mono text-caption text-ink-low">{unit.path}</p>
          </div>
          <dl className="grid grid-cols-1 gap-3 text-body">
            <DetailRow label={COPY.parent} value={parent?.path ?? '-'} />
            <DetailRow label={COPY.directMembers} value={String(unit.memberCount)} />
            <DetailRow label={COPY.unitStatus} value={unit.status} />
          </dl>
        </>
      )}
    </aside>
  );
}

function DetailRow({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-raised px-3 py-2">
      <dt className="text-ink-low">{label}</dt>
      <dd className="min-w-0 truncate font-mono text-ink">{value}</dd>
    </div>
  );
}

function StructurePanel({
  units,
  canManageUnits,
  createName,
  createParentId,
  editingUnitId,
  editName,
  editParentId,
  mutationPending,
  onCreateName,
  onCreateParent,
  onCreateUnit,
  onEditName,
  onEditParent,
  onStartEdit,
  onCancelEdit,
  onSaveUnit,
  onArchiveUnit,
  onSelectUnit,
}: {
  readonly units: readonly OrgUnitSummary[];
  readonly canManageUnits: boolean;
  readonly createName: string;
  readonly createParentId: string;
  readonly editingUnitId: string | null;
  readonly editName: string;
  readonly editParentId: string;
  readonly mutationPending: boolean;
  readonly onCreateName: (value: string) => void;
  readonly onCreateParent: (value: string) => void;
  readonly onCreateUnit: () => void;
  readonly onEditName: (value: string) => void;
  readonly onEditParent: (value: string) => void;
  readonly onStartEdit: (unit: OrgUnitSummary) => void;
  readonly onCancelEdit: () => void;
  readonly onSaveUnit: (unit: OrgUnitSummary) => void;
  readonly onArchiveUnit: (unit: OrgUnitSummary) => void;
  readonly onSelectUnit: (id: string) => void;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-line bg-card p-4">
      <PanelHeading title={COPY.structureTitle} subtitle={COPY.structureSubtitle} />
      {canManageUnits ? (
        <div className="mt-4 grid grid-cols-1 gap-3 rounded-lg border border-line bg-raised p-3 lg:grid-cols-[minmax(0,1fr)_minmax(12rem,18rem)_auto]">
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-caption font-medium text-ink-low">{COPY.structureName}</span>
            <input
              data-testid="organization-unit-create-name"
              value={createName}
              onChange={(event) => onCreateName(event.target.value)}
              placeholder={COPY.structureNamePlaceholder}
              className="min-w-0 rounded-md border border-line bg-card px-3 py-2 text-body text-ink outline-none placeholder:text-ink-faint focus:ring-2 focus:ring-forest-bright/40"
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1">
            <span className="text-caption font-medium text-ink-low">{COPY.structureParent}</span>
            <select
              value={createParentId}
              onChange={(event) => onCreateParent(event.target.value)}
              className="rounded-md border border-line bg-card px-3 py-2 text-body text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40"
            >
              <option value="">{COPY.structureRootParent}</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  - {unit.path}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-testid="organization-unit-create-submit"
            disabled={mutationPending || createName.trim().length === 0}
            onClick={onCreateUnit}
            title={COPY.structureCreateTitle}
            className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-md bg-forest px-4 text-body font-medium text-on-forest transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            <span>{COPY.create}</span>
          </button>
        </div>
      ) : null}
      {units.length === 0 ? (
        <p className="mt-4 text-body text-ink-low">{COPY.structureEmpty}</p>
      ) : (
        <div className="mt-4 divide-y divide-line-soft">
          {units.map((unit) => (
            <div
              key={unit.id}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-3 text-body"
            >
              {editingUnitId === unit.id ? (
                <div className="grid min-w-0 grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1fr)_minmax(12rem,18rem)]">
                  <input
                    data-testid={`organization-unit-edit-name-${unit.id}`}
                    value={editName}
                    onChange={(event) => onEditName(event.target.value)}
                    className="min-w-0 rounded-md border border-line bg-raised px-3 py-2 text-body text-ink outline-none focus:ring-2 focus:ring-forest-bright/40"
                  />
                  <select
                    value={editParentId}
                    onChange={(event) => onEditParent(event.target.value)}
                    className="rounded-md border border-line bg-raised px-3 py-2 text-body text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40"
                  >
                    <option value="">{COPY.structureRootParent}</option>
                    {units
                      .filter((candidate) => candidate.id !== unit.id)
                      .map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          - {candidate.path}
                        </option>
                      ))}
                  </select>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onSelectUnit(unit.id)}
                  className="grid min-w-0 grid-cols-[minmax(0,1fr)_6rem] items-center gap-4 text-left hover:text-forest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink">{unit.name}</span>
                    {unit.path !== unit.name ? (
                      <span className="block truncate font-mono text-caption text-ink-low">{unit.path}</span>
                    ) : null}
                  </span>
                  <span className="text-right font-mono text-ink">{unit.memberCount}</span>
                </button>
              )}
              {canManageUnits ? (
                <div className="flex shrink-0 items-center gap-1">
                  {editingUnitId === unit.id ? (
                    <>
                      <button
                        type="button"
                        data-testid={`organization-unit-edit-save-${unit.id}`}
                        disabled={mutationPending || editName.trim().length === 0}
                        onClick={() => onSaveUnit(unit)}
                        title={COPY.save}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-forest hover:bg-active disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                      >
                        <Check aria-hidden="true" className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={mutationPending}
                        onClick={onCancelEdit}
                        title={COPY.cancel}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-low hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                      >
                        <X aria-hidden="true" className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        data-testid={`organization-unit-edit-${unit.id}`}
                        disabled={mutationPending}
                        onClick={() => onStartEdit(unit)}
                        title={COPY.edit}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-low hover:bg-active hover:text-forest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                      >
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        data-testid={`organization-unit-archive-${unit.id}`}
                        disabled={mutationPending}
                        onClick={() => onArchiveUnit(unit)}
                        title={COPY.archive}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-low hover:bg-active hover:text-bad focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                      >
                        <Archive aria-hidden="true" className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvitationsPanel({
  invitations,
  compact = false,
}: {
  readonly invitations: readonly OrgInvitationSummary[];
  readonly compact?: boolean;
}): JSX.Element {
  return (
    <div
      data-testid="organization-invitations"
      className="rounded-lg border border-line bg-card p-4"
    >
      <PanelHeading title={COPY.invitationsTitle} subtitle={compact ? '' : COPY.invitationsSubtitle} />
      {invitations.length === 0 ? (
        <p className="mt-4 text-body text-ink-low">{COPY.noInvitations}</p>
      ) : (
        <div className="mt-4 divide-y divide-line-soft">
          {invitations.slice(0, compact ? 3 : invitations.length).map((invitation) => (
            <div key={invitation.id} className="grid grid-cols-[minmax(0,1fr)_7rem_10rem] gap-4 py-3 text-body">
              <span className="min-w-0 truncate font-mono text-ink">{invitation.email}</span>
              <span className="text-ink-mid">{invitation.role}</span>
              <span className="truncate text-right font-mono text-caption text-ink-low">
                {formatDate(invitation.expiresAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GovernancePanel({
  assignedMembers,
  limitedMembers,
  managerMembers,
}: {
  readonly assignedMembers: number;
  readonly limitedMembers: number;
  readonly managerMembers: number;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-line bg-card p-4">
      <PanelHeading title={COPY.governanceTitle} subtitle={COPY.governanceSubtitle} />
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricTile label={COPY.assigned} value={assignedMembers} icon={ClipboardCheck} />
        <MetricTile label={COPY.limitedMembers} value={limitedMembers} icon={Lock} />
        <MetricTile label={COPY.ownerAdmin} value={managerMembers} icon={ShieldCheck} />
      </div>
    </div>
  );
}

function MetricTile({ label, value, icon }: { readonly label: string; readonly value: number; readonly icon: LucideIcon }): JSX.Element {
  const IconGlyph = icon;
  return (
    <div className="flex items-center gap-3 rounded-lg bg-raised px-4 py-3">
      <IconGlyph aria-hidden="true" className="h-5 w-5 shrink-0 text-forest" />
      <div>
        <p className="text-caption text-ink-low">{label}</p>
        <p className="font-mono text-xl font-medium text-ink">{value}</p>
      </div>
    </div>
  );
}

function PanelHeading({ title, subtitle }: { readonly title: string; readonly subtitle: string }): JSX.Element {
  return (
    <header className="flex flex-col gap-1">
      <h3 className="text-body font-medium text-ink">{title}</h3>
      {subtitle.length > 0 ? <p className="text-caption text-ink-low">{subtitle}</p> : null}
    </header>
  );
}

function LoadingState({ label }: { readonly label: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-2" data-testid="organization-loading">
      {[0, 1, 2].map((idx) => (
        <Skeleton key={idx} className="h-14" />
      ))}
      <span className="sr-only">{label}</span>
    </div>
  );
}

function QueryError({ error, fallback }: { readonly error: unknown; readonly fallback: string }): JSX.Element {
  const text =
    error instanceof PlatformApiError ? `${error.code}: ${error.message}` : fallback;
  return (
    <p className="rounded-lg border border-bad-line bg-card p-3 text-body text-bad" role="alert">
      {text}
    </p>
  );
}

function pickDefaultUnitId(units: readonly OrgUnitSummary[]): string | null {
  if (units.length === 0) return null;
  const sorted = units
    .slice()
    .sort((a, b) => {
      const topLevel = Number(b.parentId === null) - Number(a.parentId === null);
      if (topLevel !== 0) return topLevel;
      if (b.memberCount !== a.memberCount) return b.memberCount - a.memberCount;
      return a.path.localeCompare(b.path);
    });
  return sorted[0]?.id ?? null;
}

function filterMembers(
  members: readonly OrgMemberSummary[],
  search: string,
  unitFilter: UnitFilter,
): OrgMemberSummary[] {
  const needle = search.trim().toLowerCase();
  return members.filter((member) => {
    if (unitFilter === 'unassigned' && member.orgUnitId !== null) return false;
    if (unitFilter !== 'all' && unitFilter !== 'unassigned' && member.orgUnitId !== unitFilter) return false;
    if (needle.length === 0) return true;
    const haystack = [
      member.displayName ?? '',
      member.email,
      member.department ?? '',
      member.orgUnitName ?? '',
      member.orgUnitPath ?? '',
      member.role,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
}

function avatarInitial(member: OrgMemberSummary): string {
  const source = member.displayName ?? member.email;
  return source.trim().slice(0, 1).toUpperCase();
}

function formatDate(raw: string): string {
  const formatted = formatTimestamp(raw);
  return formatted.length > 0 ? formatted : raw;
}

function TabIconGlyph({ tab }: { readonly tab: TabId }): JSX.Element {
  const IconGlyph: LucideIcon =
    tab === 'members' ? Users : tab === 'structure' ? Network : tab === 'invitations' ? MailOpen : ShieldCheck;
  return <IconGlyph aria-hidden="true" className="h-4 w-4" />;
}
